// src/pages/BillingManagement.js - Super Admin Billing Dashboard
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import BillingOverview from "../components/billing/BillingOverview";
import ClubUsageDetails from "../components/billing/ClubUsageDetails";
import InvoiceManagement from "../components/billing/InvoiceManagement";
import { apiService } from "../services/api";
import toast from "react-hot-toast";
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

const BillingManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  // Data states
  const [billingOverview, setBillingOverview] = useState({});
  const [clubs, setClubs] = useState([]);
  const [invoices, setInvoices] = useState([]);

  // Modal states
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showGenerateInvoicesModal, setShowGenerateInvoicesModal] =
    useState(false);
  const [showRecalculateUsageModal, setShowRecalculateUsageModal] =
    useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [clubToReactivate, setClubToReactivate] = useState(null);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [recalculatingUsage, setRecalculatingUsage] = useState(false);
  const [reactivatingClub, setReactivatingClub] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);

  useEffect(() => {
    if (user?.role === "super_admin") {
      loadBillingData();
    }
  }, [user]);

  const loadBillingData = async () => {
    try {
      const [overview, clubsData, invoicesData] = await Promise.all([
        apiService.getBillingOverview(),
        apiService.getAllClubsWithUsage(),
        apiService.getAllInvoices(),
      ]);
      setBillingOverview(overview);
      setClubs(clubsData);
      setInvoices(invoicesData.invoices);
    } catch (error) {
      console.error("Error loading billing data:", error);
      toast.error("Failed to load billing data");
    }
    setLoading(false);
  };

  const handleGenerateInvoices = async () => {
    setGeneratingInvoices(true);
    try {
      const result = await apiService.generateAllInvoices();
      toast.success(`Generated ${result.count} invoices successfully!`);
      setShowGenerateInvoicesModal(false);
      await loadBillingData();
    } catch (error) {
      toast.error("Failed to generate invoices");
    }
    setGeneratingInvoices(false);
  };

  const handleGenerateInvoicesClick = () => {
    setShowGenerateInvoicesModal(true);
  };

  const handleRecalculateUsage = async () => {
    setRecalculatingUsage(true);
    try {
      await apiService.recalculateAllUsage();
      toast.success("Usage recalculated successfully!");
      setShowRecalculateUsageModal(false);
      await loadBillingData();
    } catch (error) {
      toast.error("Failed to recalculate usage");
    }
    setRecalculatingUsage(false);
  };

  const handleRecalculateUsageClick = () => {
    setShowRecalculateUsageModal(true);
  };

  const handleReactivateClub = async () => {
    if (!clubToReactivate) return;

    setReactivatingClub(true);
    try {
      await apiService.reactivateClubSubscription(clubToReactivate.id);
      toast.success("Club subscription reactivated successfully!");
      setShowReactivateModal(false);
      setClubToReactivate(null);
      await loadBillingData();
    } catch (error) {
      toast.error("Failed to reactivate club subscription");
    }
    setReactivatingClub(false);
  };

  const handleReactivateClubClick = (club) => {
    setClubToReactivate(club);
    setShowReactivateModal(true);
  };

  const getTabContent = () => {
    switch (activeTab) {
      case "clubs":
        return <ClubsTab />;
      case "invoices":
        return <InvoicesTab />;
      default:
        return <OverviewTab />;
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      <BillingOverview overview={billingOverview} onRefresh={loadBillingData} />

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              onClick={handleGenerateInvoicesClick}
              className="h-20 flex-col"
            >
              <DocumentTextIcon className="w-6 h-6 mb-2" />
              Generate Monthly Invoices
            </Button>

            <Button
              variant="secondary"
              onClick={handleRecalculateUsageClick}
              className="h-20 flex-col"
            >
              <ChartBarIcon className="w-6 h-6 mb-2" />
              Recalculate Usage
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate("/admin/marketing")}
              className="h-20 flex-col"
            >
              Manage Plans & FAQs
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invoices.slice(0, 5).map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex justify-between items-center p-3 border border-gray-200 rounded-lg"
                >
                  <div>
                    <div className="font-medium">{invoice.club_name}</div>
                    <div className="text-sm text-gray-500">
                      {invoice.invoice_number} • ${invoice.total_amount}
                    </div>
                  </div>
                  <Badge
                    variant={
                      invoice.status === "paid"
                        ? "green"
                        : invoice.status === "overdue"
                          ? "red"
                          : "yellow"
                    }
                  >
                    {invoice.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Usage Clubs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {clubs
                .filter((club) => club.usage_tracking?.length > 0)
                .sort((a, b) => {
                  const aUsage = a.usage_tracking[a.usage_tracking.length - 1];
                  const bUsage = b.usage_tracking[b.usage_tracking.length - 1];
                  return (
                    (bUsage?.billable_players || 0) -
                    (aUsage?.billable_players || 0)
                  );
                })
                .slice(0, 5)
                .map((club) => {
                  const latestUsage =
                    club.usage_tracking[club.usage_tracking.length - 1];
                  return (
                    <div
                      key={club.id}
                      className="flex justify-between items-center p-3 border border-gray-200 rounded-lg"
                    >
                      <div>
                        <div className="font-medium">{club.name}</div>
                        <div className="text-sm text-gray-500">
                          {latestUsage.billable_players} players • $
                          {latestUsage.monthly_charge}/mo
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          // Prepare normalized club data
                          const normalizedClub = {
                            ...club,
                            club_name: club.name,
                            billable_players: latestUsage.billable_players,
                            monthly_charge: latestUsage.monthly_charge,
                          };
                          setSelectedClub(normalizedClub);
                          setShowUsageModal(true);
                        }}
                      >
                        Details
                      </Button>
                    </div>
                  );
                })}
              {clubs.filter((club) => club.usage_tracking?.length > 0)
                .length === 0 && (
                <div className="text-center text-gray-500 py-4">
                  No usage data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const ClubsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">
          Club Usage & Billing ({clubs.length} clubs)
        </h2>
        <Button onClick={handleRecalculateUsageClick}>
          Recalculate All Usage
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {clubs.map((club) => {
          // Extract subscription data
          const activeSubscription = club.club_subscriptions?.find(
            (sub) => sub.status === "active",
          );
          const planName =
            activeSubscription?.subscription_plans?.name || "No Plan";
          const pricePerPlayer =
            activeSubscription?.subscription_plans?.price_per_player_per_year ||
            0;

          // Extract usage data (latest month)
          const latestUsage =
            club.usage_tracking?.length > 0
              ? club.usage_tracking[club.usage_tracking.length - 1]
              : null;

          const activePlayersCount = latestUsage?.active_players_count || 0;
          const evaluationsCount = latestUsage?.evaluations_count || 0;
          const staffCount = latestUsage?.staff_count || 0;
          const billablePlayersCount = latestUsage?.billable_players || 0;
          const monthlyCharge = latestUsage?.monthly_charge || 0;
          const annualCharge = billablePlayersCount * pricePerPlayer;

          return (
            <Card key={club.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{club.name}</CardTitle>
                    <p className="text-gray-600">{club.location}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge
                      variant={
                        club.subscription_status === "active"
                          ? "green"
                          : club.subscription_status === "past_due"
                            ? "red"
                            : "yellow"
                      }
                    >
                      {club.subscription_status}
                    </Badge>
                    <Badge variant="blue">{planName}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-600">
                      {activePlayersCount}
                    </div>
                    <div className="text-sm text-gray-500">Active Players</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {evaluationsCount}
                    </div>
                    <div className="text-sm text-gray-500">Evaluations</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {staffCount}
                    </div>
                    <div className="text-sm text-gray-500">Staff</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      ${monthlyCharge}
                    </div>
                    <div className="text-sm text-gray-500">Monthly</div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Billing: {billablePlayersCount} players × ${pricePerPlayer}{" "}
                    = ${annualCharge}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        // Prepare club data for usage modal with normalized structure
                        const normalizedClub = {
                          ...club,
                          club_name: club.name,
                          plan_name: planName,
                          rate_per_player: pricePerPlayer,
                          billable_players: billablePlayersCount,
                          monthly_charge: monthlyCharge,
                          annual_charge: annualCharge,
                          active_players_count: activePlayersCount,
                          total_evaluations: evaluationsCount,
                          staff_count: staffCount,
                        };
                        setSelectedClub(normalizedClub);
                        setShowUsageModal(true);
                      }}
                    >
                      View Details
                    </Button>
                    {club.subscription_status !== "active" && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleReactivateClubClick(club)}
                      >
                        Reactivate
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const InvoicesTab = () => (
    <InvoiceManagement invoices={invoices} onUpdate={loadBillingData} />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  if (user?.role !== "super_admin") {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">
          Access Denied
        </h3>
        <p className="mt-1 text-gray-500">
          Only super administrators can access billing management.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Billing Management
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor usage, generate invoices, and manage subscriptions
          </p>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleRecalculateUsageClick}>
            <ChartBarIcon className="w-4 h-4 mr-2" />
            Recalculate Usage
          </Button>
          <Button onClick={handleGenerateInvoicesClick}>
            <DocumentTextIcon className="w-4 h-4 mr-2" />
            Generate Invoices
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Monthly Revenue
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  ${billingOverview.monthly_revenue?.toFixed(2) || "0.00"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-slate-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Players
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {billingOverview.total_billable_players || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Pending Invoices
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {invoices
                    ? invoices.filter((inv) => inv.status === "pending").length
                    : 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-indigo-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Active Clubs
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    clubs.filter(
                      (club) => club.subscription_status === "active",
                    ).length
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "overview", name: "Overview", icon: ChartBarIcon },
            { id: "clubs", name: "Clubs", icon: UserGroupIcon },
            { id: "invoices", name: "Invoices", icon: DocumentTextIcon },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? "border-slate-500 text-slate-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                `}
              >
                <Icon
                  className={`
                  -ml-0.5 mr-2 h-5 w-5
                  ${
                    activeTab === tab.id
                      ? "text-slate-500"
                      : "text-gray-400 group-hover:text-gray-500"
                  }
                `}
                />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">{getTabContent()}</div>

      {/* Usage Details Modal */}
      {showUsageModal && selectedClub && (
        <Modal
          title={`Usage Details - ${
            selectedClub.club_name || selectedClub.name
          }`}
          onClose={() => {
            setShowUsageModal(false);
            setSelectedClub(null);
          }}
          size="lg"
        >
          <ClubUsageDetails
            club={selectedClub}
            onClose={() => {
              setShowUsageModal(false);
              setSelectedClub(null);
            }}
            onUpdate={loadBillingData}
          />
        </Modal>
      )}

      {/* Generate Invoices Confirmation Modal */}
      <ConfirmationModal
        isOpen={showGenerateInvoicesModal}
        onClose={() => setShowGenerateInvoicesModal(false)}
        onConfirm={handleGenerateInvoices}
        title="Generate Monthly Invoices"
        message="Generate invoices for all clubs for the current billing period? This will create invoices based on current usage data."
        confirmText="Generate Invoices"
        loading={generatingInvoices}
      />

      {/* Recalculate Usage Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRecalculateUsageModal}
        onClose={() => setShowRecalculateUsageModal(false)}
        onConfirm={handleRecalculateUsage}
        title="Recalculate Usage"
        message="Recalculate usage for all clubs for the current month? This will update all usage metrics and charges."
        confirmText="Recalculate Usage"
        loading={recalculatingUsage}
      />

      {/* Reactivate Club Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReactivateModal}
        onClose={() => {
          setShowReactivateModal(false);
          setClubToReactivate(null);
        }}
        onConfirm={handleReactivateClub}
        title="Reactivate Club Subscription"
        message={
          clubToReactivate
            ? `Reactivate ${clubToReactivate.name}'s subscription? This will restore their access and billing.`
            : "Reactivate this club's subscription?"
        }
        confirmText="Reactivate"
        loading={reactivatingClub}
      />
    </div>
  );
};

export default BillingManagement;
